import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Box, Typography } from '@mui/material';
import './App.css';
import Navbar from './Navbar';
import Footer from './components/Footer';
import Home from './components/Home';
import About from './components/About';
import Login from './components/Login';
import CreateAccount from './components/CreateAccount';
import VerifyEmailSplash from './components/VerifyEmailSplash';
import VerifyEmail from './components/VerifyEmail';
import MyAccount from './components/MyAccount';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EditProfile from './components/EditProfile';
import AddChild from './components/AddChild';
import EditChild from './components/EditChild';
import Programs from './components/Programs';
import ProgramDetails from './components/ProgramDetails';
import RulesAgreement from './components/RulesAgreement';
import Enrollment from './components/Enrollment';
import Checkout from './components/Checkout';
import CheckoutSuccess from './components/CheckoutSuccess';
import AdminRoute from './components/AdminRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminPrograms from './components/admin/AdminPrograms';
import ProgramForm from './components/admin/ProgramForm';
import ProgramDetailsAdmin from './components/admin/ProgramDetailsAdmin';
import ProgramEnrollments from './components/admin/ProgramEnrollments';
import EmailManagement from './components/admin/EmailManagement';
import AdminUsers from './components/admin/AdminUsers';

// Redirect legacy /admin/special-programs/:id[...] to unified /admin/programs/:id[...]
function AdminSpecialRedirect({ suffix = '' }) {
  const { id } = useParams();
  return <Navigate to={`/admin/programs${id ? '/' + id : ''}${suffix}`} replace />;
}

// Redirect legacy /special-programs/:id[...] links to the unified /programs/:id[...]
function SpecialRedirect({ suffix = '' }) {
  const { id } = useParams();
  return <Navigate to={`/programs/${id}${suffix}`} replace />;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

// Conditional Navbar - hides on checkout and success pages
function ConditionalNavbar() {
  const location = useLocation();
  const hideNavbar = location.pathname === '/checkout' || location.pathname === '/checkout/success';

  if (hideNavbar) {
    return null;
  }

  return <Navbar />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <ConditionalNavbar />
          <Box sx={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/login" element={<Login />} />
              <Route path="/create-account" element={<CreateAccount />} />
              <Route path="/verify-email-sent" element={<VerifyEmailSplash />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/my-account"
                element={
                  <ProtectedRoute>
                    <MyAccount />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-profile"
                element={
                  <ProtectedRoute>
                    <EditProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/children/add"
                element={
                  <ProtectedRoute>
                    <AddChild />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/children/:id/edit"
                element={
                  <ProtectedRoute>
                    <EditChild />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/programs"
                element={<Programs />}
              />
              <Route
                path="/programs/:id"
                element={<ProgramDetails />}
              />
              <Route
                path="/special-programs/:id"
                element={<SpecialRedirect />}
              />
              <Route
                path="/special-programs/:id/enroll"
                element={<SpecialRedirect suffix="/enroll" />}
              />
              <Route
                path="/special-programs/:id/rules"
                element={<SpecialRedirect suffix="/rules" />}
              />
              <Route
                path="/programs/:id/rules"
                element={
                  <ProtectedRoute>
                    <RulesAgreement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/programs/:id/enroll"
                element={
                  <ProtectedRoute>
                    <Enrollment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checkout"
                element={
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                }
              />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />

              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/programs"
                element={
                  <AdminRoute>
                    <AdminPrograms />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/programs/new"
                element={
                  <AdminRoute>
                    <ProgramForm />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/programs/:id"
                element={
                  <AdminRoute>
                    <ProgramDetailsAdmin />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/programs/:id/edit"
                element={
                  <AdminRoute>
                    <ProgramForm />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/programs/:id/enrollments"
                element={
                  <AdminRoute>
                    <ProgramEnrollments />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/email"
                element={
                  <AdminRoute>
                    <EmailManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminUsers />
                  </AdminRoute>
                }
              />

              {/* Legacy special-programs routes → unified programs routes */}
              <Route path="/admin/special-programs" element={<Navigate to="/admin/programs" replace />} />
              <Route path="/admin/special-programs/new" element={<Navigate to="/admin/programs/new" replace />} />
              <Route path="/admin/special-programs/:id" element={<AdminSpecialRedirect />} />
              <Route path="/admin/special-programs/:id/edit" element={<AdminSpecialRedirect suffix="/edit" />} />
              <Route path="/admin/special-programs/:id/enrollments" element={<AdminSpecialRedirect suffix="/enrollments" />} />

              {/* Redirect any unknown routes to home */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Box>
          <Footer />
        </Box>
      </Router>
    </AuthProvider>
  );
}

export default App;